import React, { useState, useEffect, useMemo } from 'react';
import './BookDirectory.scss';

const FILTER_CONFIG_BIBLE = {
  testament: { label: 'Testament', options: ['Old', 'New'] },
  genre: { label: 'Genre', options: ['Law', 'History', 'Wisdom', 'Prophecy', 'Gospel', 'Epistle', 'Apocalyptic'] },
  audience: { label: 'Audience', options: ['Israel', 'Nineveh', 'All Nations', 'The Church', 'Individual'] }
};

const SESSION_STORAGE_FILTERS_KEY_BIBLE = 'bibleBooksDirectoryFiltersV1';

const getDefaultFiltersBible = () => {
  const defaults = {};
  for (const key in FILTER_CONFIG_BIBLE) {
    defaults[key] = [...FILTER_CONFIG_BIBLE[key].options];
  }
  return defaults;
};

const BookDirectory = ({ allBooks, guessedBooks, show, onClose, onBookSelect }) => {
  const [filters, setFilters] = useState(() => {
    try {
      const storedFilters = sessionStorage.getItem(SESSION_STORAGE_FILTERS_KEY_BIBLE);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        let isValid = true;
        for (const key in FILTER_CONFIG_BIBLE) {
          if (!parsedFilters[key] || !Array.isArray(parsedFilters[key])) {
            isValid = false;
            break;
          }
          parsedFilters[key] = parsedFilters[key].filter(opt => FILTER_CONFIG_BIBLE[key].options.includes(opt));
        }
        if (isValid) return parsedFilters;
      }
    } catch (error) {
      console.error("Error reading Bible book filters from session storage:", error);
    }
    return getDefaultFiltersBible();
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_FILTERS_KEY_BIBLE, JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving Bible book filters to session storage:", error);
    }
  }, [filters]);

  const handleFilterChange = (categoryKey, option, isRightClick = false) => {
    setFilters(prevFilters => {
      if (isRightClick) {
        // Right click: Turn off all options in this category except the clicked one
        // and ensure the clicked one is ON
        return {
          ...prevFilters,
          [categoryKey]: [option],
        };
      } else {
        // Left click: Toggle the clicked option on/off
        const currentCategorySelections = prevFilters[categoryKey] || [];
        const newCategorySelections = currentCategorySelections.includes(option)
          ? currentCategorySelections.filter(item => item !== option)
          : [...currentCategorySelections, option];
        
        return {
          ...prevFilters,
          [categoryKey]: newCategorySelections,
        };
      }
    });
  };

  const handleResetAllFilters = () => {
    setFilters(getDefaultFiltersBible());
  };

  const filteredAndSortedBooks = useMemo(() => {
    if (!allBooks || allBooks.length === 0) return [];

    return allBooks
      .filter(book => {
        for (const categoryKey in filters) {
          const selectedOptions = filters[categoryKey];

          if (FILTER_CONFIG_BIBLE[categoryKey] && FILTER_CONFIG_BIBLE[categoryKey].options.length > 0 && selectedOptions.length === 0) {
            return false; 
          }
          
          if (FILTER_CONFIG_BIBLE[categoryKey] && selectedOptions.length > 0) {
            let bookMatchesCategory = false;
            const bookValue = String(book[categoryKey]);

            if (selectedOptions.includes(bookValue)) {
                bookMatchesCategory = true;
            }
            if (!bookMatchesCategory) return false;
          }
        }
        return true;
      });
  }, [allBooks, filters]);

  if (!show) {
    return null;
  }

  const handleBookClick = (bookName) => {
    if (onBookSelect) {
      onBookSelect(bookName);
    }
    onClose();
  };

  const areFiltersDefault = () => {
    const defaultFilters = getDefaultFiltersBible();
    for (const categoryKey in filters) {
        if (!FILTER_CONFIG_BIBLE[categoryKey]) continue;
        const selectedForCategory = filters[categoryKey]?.sort().join(',') || '';
        const defaultForCategory = defaultFilters[categoryKey]?.sort().join(',') || '';
        if (selectedForCategory !== defaultForCategory) {
            return false;
        }
    }
    return true;
  };
  const isAnyFilterActive = !areFiltersDefault();

  return (
    <div className="book-directory__overlay" onClick={onClose}>
      <div className="book-directory__panel" onClick={(e) => e.stopPropagation()}>
        <button className="book-directory__close-button" onClick={onClose}>✕</button>
        <div className="book-directory__header">
            <h2>Book Directory</h2>
            {isAnyFilterActive && (
                <button onClick={handleResetAllFilters} className="book-directory__reset-button">
                    Reset Filters
                </button>
            )}
        </div>

        <div className="book-directory__filters">
          {Object.entries(FILTER_CONFIG_BIBLE).map(([categoryKey, config]) => (
            <div key={categoryKey} className="book-directory__filter-category">
              <span className="book-directory__filter-label">{config.label}:</span>
              <div className="book-directory__filter-options">
                {config.options.map(option => (
                  <button
                    key={option}
                    className={`book-directory__filter-button ${
                      filters[categoryKey]?.includes(option) ? 'book-directory__filter-button--selected' : ''
                    }`}
                    onClick={() => handleFilterChange(categoryKey, option, false)}
                    onContextMenu={(e) => {
                      e.preventDefault(); // Prevent default context menu
                      handleFilterChange(categoryKey, option, true);
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <ul className="book-directory__list">
          {filteredAndSortedBooks.length > 0 ? (
            filteredAndSortedBooks.map((book) => (
              <li
                key={book.name} 
                className={`book-directory__list-item ${
                  guessedBooks.includes(book.name) ? 'book-directory__list-item--guessed' : ''
                }`}
                onClick={() => {
                  if (!guessedBooks.includes(book.name)) {
                    handleBookClick(book.name);
                  }
                }}
                style={!guessedBooks.includes(book.name) ? { cursor: 'pointer' } : {}}
              >
                <div className="book-directory__item-content">
                  <span className="book-directory__item-name">{book.name}</span>
                  <div className="book-directory__item-details">
                    <span className="book-directory__item-detail">{book.testament}</span>
                    <span className="book-directory__item-detail">{book.genre}</span>
                    <span className="book-directory__item-detail">{book.audience}</span>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="book-directory__list-item book-directory__list-item--empty">No books match the current filters.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default BookDirectory;